"use client";

import { useEffect, useRef, useState } from "react";

const SIZE = 232;
const STROKE = 16;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const DURATION_MS = 1400;
const START_DELAY_MS = 50;
/** Bezier-Naeherung von easeOutCubic() unten (uebliche Konvention, siehe easings.net) -
 * treibt die CSS-Transition auf `stroke-dashoffset`, damit Ring und hochzaehlende Zahl
 * derselben wahrgenommenen Kurve folgen statt zweier leicht unterschiedlicher. */
const RING_EASING = "cubic-bezier(0.33, 1, 0.68, 1)";

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Entruckelt (Erik-Feedback 2026-07-23, Feedback-Runde 17 Punkt 7): Ring-Füllstand läuft
 * über eine reine CSS-`transition` auf `stroke-dashoffset` (kein React-Re-Render während
 * der Animation), die hochzählende Zahl über `requestAnimationFrame`, schreibt aber direkt
 * per Ref in `textContent` statt React-State zu setzen - beide folgen derselben
 * `RING_EASING`-Kurve, damit Ring und Zahl sichtbar synchron bleiben.
 *
 * **2026-07-30 (backlog.md Punkte 8-9, Erik-Live-Report)**: der bisherige separate
 * Glow-Ring (ein dritter `<circle>`, der nach Animationsende per `drop-shadow`+`opacity`
 * einblendete) wurde ersatzlos entfernt - sein 12px-Blur bei voller Deckkraft blutete weit
 * über die Ringkontur hinaus und las sich als "farbiger Hintergrund erscheint, sobald der
 * Ring stehen bleibt", nicht als beabsichtigter Nachglanz. Der Gradient hatte zusaetzlich
 * einen hellen Zwischen-Stop (`#ECEAE3`, dieselbe Farbe wie die Karten-Hintergruende) auf
 * 50% - das verwaschte Navy und Gold genau in der Ringmitte zu einem blassen/milchigen
 * Uebergang statt eines kraeftigen. Beides ersetzt durch einen einfachen satten
 * Zwei-Farb-Gradient (Navy -> Gold) ohne Glow-Effekt.
 */
export function ScoreRing({ score }: { score: number }) {
  const clamped = Math.max(0, Math.min(100, score));
  const [filled, setFilled] = useState(false);
  const numberRef = useRef<HTMLSpanElement>(null);
  const frameRef = useRef<number | null>(null);

  // Re-triggert ueber `score` (nicht nur beim Mount): der aufrufende score-section.tsx
  // setzt zusaetzlich `key={symbol}` auf diese Komponente, ein echter Remount bei neuer
  // Aktie ist damit der Regelfall - dieser Effect ist eine zweite Absicherung, falls eine
  // Instanz doch einmal wiederverwendet statt neu gemountet wird.
  useEffect(() => {
    if (numberRef.current) numberRef.current.textContent = "0";

    // setState darf laut react-hooks/set-state-in-effect nicht synchron im Effect-Body
    // stehen (siehe Originalversion dieser Komponente) - alle State-Updates liegen deshalb
    // im setTimeout-Callback, der zusaetzlich zuverlaessig einen Paint des 0%-Zustands vor
    // dem eigentlichen Animationsstart garantiert (kein direkter rAF-Start: der wird in
    // Hintergrund-/inaktiven Tabs gedrosselt und koennte den Start beliebig verzoegern).
    const startTimeout = setTimeout(() => {
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduceMotion) {
        setFilled(true);
        if (numberRef.current) numberRef.current.textContent = String(clamped);
        return;
      }

      setFilled(true);
      let start: number | null = null;
      function tick(ts: number) {
        if (start === null) start = ts;
        const t = Math.min(1, (ts - start) / DURATION_MS);
        if (numberRef.current) numberRef.current.textContent = String(Math.round(clamped * easeOutCubic(t)));
        if (t < 1) {
          frameRef.current = requestAnimationFrame(tick);
        }
      }
      frameRef.current = requestAnimationFrame(tick);
    }, START_DELAY_MS);

    return () => {
      clearTimeout(startTimeout);
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [score, clamped]);

  const fillFraction = (filled ? clamped : 0) / 100;
  const offset = CIRCUMFERENCE * (1 - fillFraction);

  return (
    <div className="relative shrink-0" style={{ width: SIZE, height: SIZE }}>
      <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
        <defs>
          <linearGradient id="scoreRingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#16265C" />
            <stop offset="100%" stopColor="#C6A15B" />
          </linearGradient>
        </defs>
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="#ECEAE3"
          strokeWidth={STROKE}
        />
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="url(#scoreRingGradient)"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          className="[will-change:stroke-dashoffset] transition-[stroke-dashoffset] motion-reduce:transition-none"
          style={{ transitionDuration: `${DURATION_MS}ms`, transitionTimingFunction: RING_EASING }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="flex items-baseline gap-1">
          <span ref={numberRef} className="font-serif text-6xl text-ink tabular-nums">
            0
          </span>
          <span className="text-base text-muted-foreground">/ 100</span>
        </div>
      </div>
    </div>
  );
}
